import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Col, Form, Row } from 'antd';

import { getErrorMessageFromAPI } from '@/utilz';
import { CREATE_OVER_CHARGE_CLAIM_DEFAULT_VALUE } from '@/contants/ClaimsEnums';
import AlertModal from '@/views/components/Modals/AlertModal';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import ContentSpinner from '@/views/components/Spinners/ContentSpinner';

import { APP_ROUTES } from '../../../../contants/routes';
import { setAppLoader, setPageBlocker, setWarningModal } from '../../../../redux/common/CommonActions';
import {
  checkDuplicateOverchargeItem,
  overChargeClaimsDetailsSectionLocalToAPI,
  overChargeClaimsItemsSectionLocalToAPI,
  overChargeClaimsNotesSectionLocalToAPI,
  overChargeClaimsSettlementsSectionLocalToAPI,
} from '../../../../utilz/OverChargeClaimUtils';
import SaveCancelButtons from '../../../components/Buttons/SaveCancelButtons';
import FormLabel from '../../../components/Labels/FormLabel';
import SecondaryLabel from '../../../components/Labels/SecondaryLabel';
import Paper from '../../../components/Paper/Paper';
import { useGetOCClaimInitialValues } from '../hooks';
import CreateOverChargeClaimTab from './CreateClaimTabs/CreateOverChargeClaimTab';
import { useCheckDuplicateOCClaim, useCreateOCClaim } from './hooks';

const CreateOverChargeClaimsScreen = () => {
  const selectedCompany = useSelector((state) => state.auth.selectedCompany);
  const changeCompany = useSelector((state) => state.auth.changeCompany);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const carrierKey = useMemo(() => searchParams.get('carrierKey'), [searchParams]);
  const [activeKey, setActiveKey] = useState(['1', '2', '3', '4']);
  const [itemList, setItemList] = useState(CREATE_OVER_CHARGE_CLAIM_DEFAULT_VALUE.ITEMS);
  const [notesList, setNotesList] = useState(CREATE_OVER_CHARGE_CLAIM_DEFAULT_VALUE.NOTES);
  const [settlementList, setSettlementList] = useState(CREATE_OVER_CHARGE_CLAIM_DEFAULT_VALUE.SETTLEMENT);
  const [duplicateClaimText, setDuplicateClaimText] = useState(false);
  const navigate = useNavigate();
  const [initialForm] = Form.useForm();
  const [detailsForm] = Form.useForm();
  const isInitialRenderRef = useRef(true);
  const { mutateAsync: createClaim } = useCreateOCClaim();
  const { mutateAsync: checkDuplicateClaim } = useCheckDuplicateOCClaim({ showLoader: false });

  const { data: initialValues, isFetching: isInitialValuesLoading } = useGetOCClaimInitialValues({
    carrierKey,
    companyId: selectedCompany?.companyId,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO: fix
  useEffect(() => {
    if (changeCompany.success && !isInitialRenderRef.current) {
      initialForm.resetFields();
      detailsForm.resetFields();
      setItemList(CREATE_OVER_CHARGE_CLAIM_DEFAULT_VALUE.ITEMS);
      setNotesList(CREATE_OVER_CHARGE_CLAIM_DEFAULT_VALUE.NOTES);
      setSettlementList(CREATE_OVER_CHARGE_CLAIM_DEFAULT_VALUE.SETTLEMENT);
    }
    isInitialRenderRef.current = false;
  }, [changeCompany]);

  const handleCreateSuccess = (fbocKey) => {
    dispatch(setPageBlocker(false));
    setTimeout(() => {
      navigate(APP_ROUTES.OVERCHARGE_CLAIMS, {
        state: {
          showEmailPromptForFbOcKey: fbocKey,
        },
      });
      // biome-ignore lint/style/noMagicNumbers: keep aligned with sibling create-flow timing pattern
    }, 500);
  };

  const handleFormProviderSubmit = async (form) => {
    if (form === 'saveButtonForm') {
      try {
        dispatch(setAppLoader(true));
        await detailsForm.validateFields();
        const completedItems = itemList?.filter((item) => !item.isNew);
        if (completedItems?.length > 0) {
          let formData = overChargeClaimsDetailsSectionLocalToAPI(detailsForm.getFieldValue());
          formData.claimItem = itemList?.filter((item) => !item.isNew);
          formData.claimComment = notesList?.filter((item) => !item.isNew);
          formData.claimSettlement = settlementList?.filter((item) => !item.isNew);
          formData = overChargeClaimsItemsSectionLocalToAPI(formData);
          formData = overChargeClaimsNotesSectionLocalToAPI(formData);
          formData = overChargeClaimsSettlementsSectionLocalToAPI(formData);
          formData.fbOcKey = 0;
          const duplicateValue = await checkDuplicateClaim(formData);
          const { isHavingDuplicateItem, duplicateMessage } = checkDuplicateOverchargeItem(
            formData.claimItem,
            String(duplicateValue),
          );
          if (isHavingDuplicateItem) {
            setDuplicateClaimText(duplicateMessage);
            return;
          }
          await createClaim(formData, {
            onSuccess: handleCreateSuccess,
          });
        } else {
          dispatch(
            setWarningModal({
              description: 'Minimum one item is required!',
              open: true,
            }),
          );
        }
      } catch (error) {
        const errorMessage = getErrorMessageFromAPI(error);
        if (errorMessage && typeof errorMessage === 'string') {
          showNotification({
            message: errorMessage,
            type: NOTIFICATION_TYPES.ERROR,
          });
        }
      } finally {
        dispatch(setAppLoader(false));
      }
    }
  };

  const recoverySumTotal = useMemo(() => {
    if (!Array.isArray(itemList)) {
      return 0;
    }

    return itemList.reduce((acc, item) => {
      const amount = Number(item?.recoveryAmount ?? 0);
      return acc + (Number.isNaN(amount) ? 0 : amount);
    }, 0);
  }, [itemList]);

  const overchargeSumTotal = useMemo(() => {
    if (!Array.isArray(itemList)) {
      return 0;
    }

    return itemList.reduce((acc, item) => {
      const amount = Number(item?.overchargeAmount ?? 0);
      return acc + (Number.isNaN(amount) ? 0 : amount);
    }, 0);
  }, [itemList]);

  useEffect(() => {
    detailsForm.setFieldValue('recoverySum', recoverySumTotal);
  }, [recoverySumTotal, detailsForm]);

  useEffect(() => {
    detailsForm.setFieldValue('overchargeSum', overchargeSumTotal);
  }, [overchargeSumTotal, detailsForm]);

  return (
    <Form.Provider onFormFinish={handleFormProviderSubmit}>
      <Row className="flex-direction-column flex-nowrap overflow-hidden h-100">
        <Col className="mb-15">
          <Paper className="px-15 py-10 border-radius-bottom-none">
            <Row align="middle" justify="space-between">
              <Col>
                <Form name="initialForm" form={initialForm}>
                  <Row gutter={15}>
                    <Col>
                      <FormLabel>Claim No : </FormLabel>
                      <SecondaryLabel className="f-14 fw-600">0</SecondaryLabel>
                    </Col>
                    <Col>
                      <FormLabel>Currency : </FormLabel>
                      <SecondaryLabel className="f-14 fw-600">USD</SecondaryLabel>
                    </Col>
                  </Row>
                </Form>
              </Col>
              <Col>
                <Row align="middle" gutter={15}>
                  <Col>
                    <Form name="saveButtonForm">
                      <SaveCancelButtons onCancel={() => navigate(-1)} idSuffix="shipment" />
                    </Form>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Paper>
        </Col>
        <Col className="flex-1 custom-scroll-bar">
          {isInitialValuesLoading ? (
            <ContentSpinner size="large" />
          ) : (
            <CreateOverChargeClaimTab
              activeKey={activeKey}
              setActiveKey={setActiveKey}
              detailsForm={detailsForm}
              itemList={itemList}
              setItemList={setItemList}
              notesList={notesList}
              setNotesList={setNotesList}
              settlementList={settlementList}
              setSettlementList={setSettlementList}
              initialValues={initialValues}
            />
          )}
        </Col>
      </Row>
      <AlertModal
        open={Boolean(duplicateClaimText)}
        description={duplicateClaimText}
        onOk={() => setDuplicateClaimText(false)}
      />
    </Form.Provider>
  );
};

export default CreateOverChargeClaimsScreen;
